import { useCallback } from 'react';

import {
   ConfidentialAccessLevel,
   ConfidentialityConfiguration,
} from '@dreryk/egabinet-medical-data-confidentiality-frontend-api';

import { Translate } from '@dreryk/react-common';
import { StrongText, Table } from '@dreryk/react-components';

import { generalSettingsTranslationKeys } from '../../../translations';

import { ConfidentialityEditModalOpener } from './ConfidentialityEdit/ConfidentialityEditModal.opener';
import { useConfidentialitySettingRows, useConfidentialitySettingsColumns } from './ConfidentialitySettings.hooks';
import { ConfidentialitySettingsLoader } from './ConfidentialitySettings.loader';
import { StyledConfidentialitySettings, StyledConfidentialityTable } from './ConfidentialitySettings.styled';
import { CONFIDENTIALITY_ACCESS_LEVELS, ConfidentialitySettingRow } from './ConfidentialitySettings.types';
import { getAccessDurationHours } from './ConfidentialitySettings.utils';
import { PrescriptionIssuingAccessField } from './PrescriptionIssuingAccessField';

const t = generalSettingsTranslationKeys.confidentialitySettings;

const keyExtractor = (bean: ConfidentialitySettingRow): string => bean.accessLevel;

type ConfidentialitySettingsTableProps = {
   confidentialityConfiguration: ConfidentialityConfiguration;
   onEdit: VoidFunction;
};

const ConfidentialitySettingsTable = (props: ConfidentialitySettingsTableProps): JSX.Element => {
   const getDurationHours = useCallback(
      (accessLevel: ConfidentialAccessLevel) => getAccessDurationHours(props.confidentialityConfiguration, accessLevel),
      [props.confidentialityConfiguration]
   );

   const rows = useConfidentialitySettingRows(CONFIDENTIALITY_ACCESS_LEVELS, getDurationHours);
   const columns = useConfidentialitySettingsColumns({ onEdit: props.onEdit });

   return (
      <StyledConfidentialitySettings>
         <StyledConfidentialityTable>
            <StrongText testId={'confidentialityTableLabel'}>
               <Translate>{t.table.label}</Translate>
            </StrongText>
            <Table<ConfidentialitySettingRow>
               testId={'confidentialitySettings'}
               columns={columns}
               dataConfiguration={{ data: rows }}
               keyExtractor={keyExtractor}
               pageSize={Infinity}
               enableStaticSorting={false}
               compact
            />
         </StyledConfidentialityTable>
         <PrescriptionIssuingAccessField confidentialityConfiguration={props.confidentialityConfiguration} />
      </StyledConfidentialitySettings>
   );
};

export const ConfidentialitySettings = (): JSX.Element => {
   return (
      <ConfidentialitySettingsLoader>
         {({ confidentialityConfiguration }) => (
            <ConfidentialityEditModalOpener confidentialityConfiguration={confidentialityConfiguration}>
               {({ openModal }) => (
                  <ConfidentialitySettingsTable
                     confidentialityConfiguration={confidentialityConfiguration}
                     onEdit={openModal}
                  />
               )}
            </ConfidentialityEditModalOpener>
         )}
      </ConfidentialitySettingsLoader>
   );
};
